import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PROCESS_QUERY_PARAM, openProcessHref } from "@/shared/lib/processNavigation";
import { Building2, Lock, Mail, User, UserPlus } from "lucide-react";
import { toast } from "sonner";
import {
  LoginAlert,
  LoginButton,
  LoginField,
  LoginHelp,
  LoginLayout,
  LoginPasswordField,
  LoginSelect,
  LoginSubmit,
} from "@scorvia/design-system/login";

import { useAuth } from "../context/AuthContext";
import { useBranding } from "@/features/branding/hooks/useBranding";
import {
  listOrgaosForSignup,
  requestPasswordReset,
  signIn,
  signUp,
  type OrgaoOption,
} from "../services/auth.service";
import { signInSchema, signUpSchema } from "../types";
import { PAINEL_LOGIN, marcaDoBranding } from "../components/painelLogin";

const AuthPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { branding } = useBranding();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgaos, setOrgaos] = useState<OrgaoOption[]>([]);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nome, setNome] = useState("");
  const [orgaoId, setOrgaoId] = useState("");
  const [role, setRole] = useState("");

  useEffect(() => {
    if (!user) return;
    const pid = new URLSearchParams(location.search).get(PROCESS_QUERY_PARAM);
    navigate(pid ? openProcessHref(pid) : "/", { replace: true });
  }, [user, navigate, location.search]);

  useEffect(() => {
    listOrgaosForSignup()
      .then(setOrgaos)
      .catch(() => setOrgaos([]));
  }, []);

  const trocarModo = (acao: () => void) => {
    setErro("");
    setAviso("");
    acao();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    setAviso("");
    const parsed = signInSchema.safeParse({ email, password });
    if (!parsed.success) {
      setErro(parsed.error.errors[0].message);
      return;
    }
    setLoading(true);
    try {
      await signIn(parsed.data.email, parsed.data.password);
      toast.success("Login realizado com sucesso!");
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Erro ao entrar");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    setAviso("");
    const parsed = signUpSchema.safeParse({ email, password, nome, orgaoId, role });
    if (!parsed.success) {
      setErro(parsed.error.errors[0].message);
      return;
    }
    setLoading(true);
    try {
      await signUp({
        email: parsed.data.email,
        password: parsed.data.password,
        nome: parsed.data.nome,
        orgaoId: parsed.data.orgaoId,
        role: parsed.data.role,
      });
      setAviso("Cadastro realizado! Verifique seu email para confirmar.");
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Erro ao cadastrar");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    setAviso("");
    if (!email.trim()) {
      setErro("Informe seu email.");
      return;
    }
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setAviso("Email de recuperação enviado! Verifique sua caixa de entrada.");
      setIsForgotPassword(false);
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Erro ao enviar email");
    } finally {
      setLoading(false);
    }
  };

  const heading = isForgotPassword ? "Recuperar senha" : isLogin ? "Acessar sistema" : "Criar conta";
  const subtitle = isForgotPassword
    ? "Informe seu email para receber o link de redefinição."
    : isLogin
      ? "Entre com suas credenciais."
      : "Preencha os dados para se cadastrar.";

  const campoEmail = (
    <LoginField
      id="email"
      label="Email"
      icon={<Mail />}
      type="email"
      autoComplete="email"
      placeholder="seu@email.gov.br"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      required
    />
  );

  return (
    <LoginLayout
      {...PAINEL_LOGIN}
      brand={marcaDoBranding(branding)}
      skipLink={{ href: "#email", label: "Ir para o formulário de acesso" }}
      heading={heading}
      subtitle={subtitle}
      context={branding.orgao_nome || undefined}
      contextIcon={branding.orgao_nome ? <Building2 aria-hidden="true" /> : undefined}
      alert={erro || undefined}
      help={
        isForgotPassword ? (
          <p className="ds-login__actions">
            <LoginButton variant="link" onClick={() => trocarModo(() => setIsForgotPassword(false))}>
              ← Voltar ao login
            </LoginButton>
          </p>
        ) : (
          <LoginHelp icon={<UserPlus />} title={isLogin ? "Não tem conta?" : "Já tem conta?"}>
            <LoginButton variant="link" onClick={() => trocarModo(() => setIsLogin(!isLogin))}>
              {isLogin ? "Cadastre-se" : "Faça login"}
            </LoginButton>
          </LoginHelp>
        )
      }
    >
      {aviso && <LoginAlert variant="success">{aviso}</LoginAlert>}

      {isForgotPassword ? (
        <form className="ds-login__form" onSubmit={handleForgotPassword}>
          {campoEmail}
          <LoginSubmit loading={loading} loadingLabel="Aguarde…">
            Enviar link de recuperação
          </LoginSubmit>
        </form>
      ) : (
        <form className="ds-login__form" onSubmit={isLogin ? handleLogin : handleSignup}>
          {!isLogin && (
            <>
              <LoginField
                id="nome"
                label="Nome completo"
                icon={<User />}
                placeholder="Seu nome"
                autoComplete="name"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
              />
              <LoginSelect
                id="orgao"
                label="Órgão vinculado"
                value={orgaoId}
                onChange={(e) => setOrgaoId(e.target.value)}
              >
                <option value="" disabled>
                  Selecione o órgão
                </option>
                {orgaos.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nome}
                  </option>
                ))}
              </LoginSelect>
              <LoginSelect
                id="role"
                label="Permissão"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="" disabled>
                  Selecione a permissão
                </option>
                <option value="admin">Administrador</option>
                <option value="gestor">Gestor</option>
                <option value="consulta">Consulta</option>
              </LoginSelect>
            </>
          )}

          {campoEmail}

          <LoginPasswordField
            id="password"
            label="Senha"
            icon={<Lock />}
            autoComplete={isLogin ? "current-password" : "new-password"}
            placeholder={isLogin ? "Digite sua senha" : "Mínimo 6 caracteres"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            capsLockWarning="Caps Lock está ativado"
            labelAside={
              isLogin ? (
                <LoginButton
                  variant="link"
                  onClick={() => trocarModo(() => setIsForgotPassword(true))}
                >
                  Esqueceu sua senha?
                </LoginButton>
              ) : undefined
            }
          />

          <LoginSubmit loading={loading} loadingLabel="Aguarde…">
            {isLogin ? "Entrar" : "Cadastrar"}
          </LoginSubmit>
        </form>
      )}
    </LoginLayout>
  );
};

export default AuthPage;

